import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { AbstimmungExportstand, ExportEintrag, ExportformatWert } from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';

/**
 * Anbindung der Exporte (§27).
 *
 * Der **Download** läuft bewusst nicht über {@link apiAnfrage}, sondern über einen gewöhnlichen
 * Verweis auf {@link exportDateiPfad} — wie beim Token-CSV seit S2. Ein `fetch` müsste den
 * Dateiinhalt erst vollständig in den Arbeitsspeicher holen, einen Objekt-URL erzeugen und einen
 * unsichtbaren Verweis anklicken; der Browser kann das von sich aus besser, einschließlich
 * Fortschrittsanzeige und Speicherort-Dialog.
 */

const VERANSTALTUNGEN = '/api/v1/admin/veranstaltungen';
const ABSTIMMUNGEN = '/api/v1/admin/abstimmungen';
const EXPORTE = '/api/v1/admin/exporte';

export const EXPORTE_SCHLUESSEL = ['admin', 'exporte'] as const;

/** Pfad zum Herunterladen einer Exportdatei. Für `<a href>`; kein `fetch`. */
export function exportDateiPfad(exportId: string): string {
  return `${EXPORTE}/${exportId}/datei`;
}

/** Exportstand je Format einer einzelnen Abstimmung (§27.3). */
export function useAbstimmungExportstand(
  abstimmungId: string,
  aktiv: boolean,
): UseQueryResult<AbstimmungExportstand, ApiFehler> {
  return useQuery<AbstimmungExportstand, ApiFehler>({
    queryKey: [...EXPORTE_SCHLUESSEL, 'abstimmung', abstimmungId],
    queryFn: () => apiAnfrage<AbstimmungExportstand>(`${ABSTIMMUNGEN}/${abstimmungId}/exporte`),
    enabled: aktiv && abstimmungId !== '',
  });
}

/** Exportstand aller geschlossenen Abstimmungen einer Veranstaltung (§27.3). */
export function useExportstaende(
  veranstaltungId: string,
): UseQueryResult<AbstimmungExportstand[], ApiFehler> {
  return useQuery<AbstimmungExportstand[], ApiFehler>({
    queryKey: [...EXPORTE_SCHLUESSEL, 'staende', veranstaltungId],
    queryFn: () =>
      apiAnfrage<AbstimmungExportstand[]>(`${VERANSTALTUNGEN}/${veranstaltungId}/exporte/staende`),
    enabled: veranstaltungId !== '',
  });
}

/** Alle verbuchten Exporte einer Veranstaltung, auch die gelöschten (§27.3, §27.4). */
export function useVeranstaltungExporte(
  veranstaltungId: string,
): UseQueryResult<ExportEintrag[], ApiFehler> {
  return useQuery<ExportEintrag[], ApiFehler>({
    queryKey: [...EXPORTE_SCHLUESSEL, 'veranstaltung', veranstaltungId],
    queryFn: () => apiAnfrage<ExportEintrag[]>(`${VERANSTALTUNGEN}/${veranstaltungId}/exporte`),
    enabled: veranstaltungId !== '',
  });
}

function useMitAktualisierung<TVariablen>(
  fn: (variablen: TVariablen) => Promise<ExportEintrag>,
): UseMutationResult<ExportEintrag, ApiFehler, TVariablen> {
  const queryClient = useQueryClient();

  return useMutation<ExportEintrag, ApiFehler, TVariablen>({
    mutationFn: fn,
    // Erzeugen und Löschen verändern Stand und Liste zugleich. Ein gemeinsamer Präfix genügt.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: EXPORTE_SCHLUESSEL });
    },
  });
}

/** Erzeugt oder erneuert den Export einer geschlossenen Abstimmung (§27.1). */
export function useAbstimmungExportieren(): UseMutationResult<
  ExportEintrag,
  ApiFehler,
  { abstimmungId: string; format: ExportformatWert }
> {
  return useMitAktualisierung(({ abstimmungId, format }) =>
    apiAnfrage<ExportEintrag>(`${ABSTIMMUNGEN}/${abstimmungId}/exporte/${format}`, {
      methode: 'POST',
    }),
  );
}

/** Vollständiger JSON-Export einer Veranstaltung (§27.2). */
export function useVeranstaltungExportieren(): UseMutationResult<ExportEintrag, ApiFehler, string> {
  return useMitAktualisierung((veranstaltungId) =>
    apiAnfrage<ExportEintrag>(`${VERANSTALTUNGEN}/${veranstaltungId}/exporte/json`, {
      methode: 'POST',
    }),
  );
}

/** Gemeinsames PDF aller Ergebnisse einer Veranstaltung (§27.2, ÄA-6). */
export function useVeranstaltungPdfExportieren(): UseMutationResult<
  ExportEintrag,
  ApiFehler,
  string
> {
  return useMitAktualisierung((veranstaltungId) =>
    apiAnfrage<ExportEintrag>(`${VERANSTALTUNGEN}/${veranstaltungId}/exporte/pdf`, {
      methode: 'POST',
    }),
  );
}

/** Löscht die Datei; der verbuchte Vorgang bleibt erhalten (§27.4). */
export function useExportLoeschen(): UseMutationResult<ExportEintrag, ApiFehler, string> {
  return useMitAktualisierung((exportId) =>
    apiAnfrage<ExportEintrag>(`${EXPORTE}/${exportId}`, { methode: 'DELETE' }),
  );
}
